import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Ban,
  Check,
  ClipboardPaste,
  Clock,
  History,
  Package,
  Filter,
  Map as MapaIcone,
  MousePointerClick,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import type { Posicao } from '@wms/shared';
import { useQueries } from '@tanstack/react-query';
import { useMapa } from '@/hooks/consultas';
import { useContexto } from '@/hooks/useContexto';
import { api } from '@/lib/api';
import type { PosicaoResposta } from '@/lib/tipos';
import {
  useCriarEscopo,
  useEnderecosOcupados,
  useInventario,
  useRitmoContagem,
  type EnderecoOcupado,
} from '@/hooks/inventario';
import { ErroHttp } from '@/lib/api';
import { cn, compararNatural, numero, plural } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { CabecalhoPagina, Cartao, Esqueleto } from '@/componentes/ui/primitivos';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { NavInventario } from '@/componentes/inventario/NavInventario';
import { Executando } from '@/componentes/inventario/Executando';
import { AbaDeEscopo, AbaProdutos } from '@/componentes/inventario/AbasMontador';
import { CartaoEndereco, useHoverCartao, type PontoCartao } from '@/componentes/inventario/CartaoEndereco';

/**
 * Montador de escopo.
 *
 * No PBS montar escopo é digitar faixa de corredor, módulo e box num
 * formulário e apertar um botão — sem ver o que entrou, sem saber se o
 * endereço já está em outro escopo. Aqui o escopo se monta olhando o CD:
 *
 *   Mapa      clique ou arraste nas ruas; abra a rua para marcar módulo,
 *             nível ou vaga no próprio rack.
 *   Filtros   rua de/até, zona, nível, só com saldo — vira uma seleção.
 *   Importar  cole a lista (Excel, WhatsApp) ou suba um CSV/TXT.
 *
 * As três alimentam a MESMA seleção, que o painel da direita resume por rua,
 * com os totais ao vivo. Nada vai para o ERP até "Criar escopo".
 *
 * Endereço que já está em escopo aberto do centro aparece riscado e não entra
 * — decisão de 07/10/2026: o PBS deixa, e o saldo é ajustado duas vezes.
 *
 * Os dados são o MESMO mapa da tela de Estoque (em cache): marcar e filtrar
 * não vai ao servidor.
 */

type Aba = 'mapa' | 'filtros' | 'importar' | 'produtos' | 'escopo';

interface Rua {
  nome: string;
  posicoes: Posicao[];
}

function chaveRua(p: Posicao): string {
  return p.corredor.trim() || '(sem rua)';
}

export function InventarioNovoEscopo(): JSX.Element {
  const { id: bruto } = useParams();
  const id = Number(bruto);
  const navegar = useNavigate();
  const detalhe = useInventario(Number.isInteger(id) && id > 0 ? id : null);
  const { centros } = useContexto();
  /*
   * O escopo pode ir para qualquer centro da EMPRESA do inventário que o
   * usuário alcança (o inventário 15 juntou 2301, 2303, 2305 e 2307). Abre no
   * centro do inventário.
   */
  const [centroEscolhido, definirCentroEscolhido] = React.useState<number | null>(null);
  const centrosDaEmpresa = centros.filter((c) => c.empresa === detalhe.data?.inventario.empresa);
  const centro = centroEscolhido ?? detalhe.data?.inventario.centro ?? null;
  const mapa = useMapa(centro);
  const ocupadosQ = useEnderecosOcupados(detalhe.data ? id : null, centro);
  const criar = useCriarEscopo(id);
  const ritmo = useRitmoContagem();

  const [aba, definirAba] = React.useState<Aba>('mapa');
  const [selecao, definirSelecao] = React.useState<Set<number>>(new Set());
  const [ruaAberta, definirRuaAberta] = React.useState<string | null>(null);
  const [confirmando, definirConfirmando] = React.useState(false);

  const ocupados = React.useMemo(() => {
    const m = new Map<number, EnderecoOcupado>();
    for (const o of ocupadosQ.data?.enderecos ?? []) m.set(o.localizador, o);
    return m;
  }, [ocupadosQ.data]);

  const posicoes = React.useMemo(
    () => (mapa.data?.posicoes ?? []).filter((p) => p.ativo),
    [mapa.data],
  );
  const porId = React.useMemo(() => new Map(posicoes.map((p) => [p.localizador, p])), [posicoes]);
  const ruas = React.useMemo<Rua[]>(() => {
    const g = new Map<string, Posicao[]>();
    for (const p of posicoes) {
      const k = chaveRua(p);
      const l = g.get(k) ?? [];
      l.push(p);
      g.set(k, l);
    }
    return [...g.entries()]
      .sort(([a], [b]) => compararNatural(a, b))
      .map(([nome, ps]) => ({ nome, posicoes: ps }));
  }, [posicoes]);

  /**
   * Adiciona ou tira. Endereço que já está em outro escopo entra também: o
   * mesmo local pode estar em quantos escopos precisar (pedido do CD em
   * 14/10/2026). `ocupados` só serve para avisar.
   */
  const aplicar = React.useCallback(
    (lista: Iterable<number>, modo: 'add' | 'remove') =>
      definirSelecao((atual) => {
        const novo = new Set(atual);
        for (const l of lista) {
          if (modo === 'add') novo.add(l);
          else novo.delete(l);
        }
        return novo;
      }),
    [],
  );

  const selecionadosEmOutro = React.useMemo(
    () => [...selecao].filter((l) => ocupados.has(l)).length,
    [selecao, ocupados],
  );

  if (!Number.isInteger(id) || id <= 0) {
    return <EstadoVazio titulo="Inventário inválido" />;
  }
  if (detalhe.isError) return <EstadoErro erro={detalhe.error} />;
  if (mapa.isError) return <EstadoErro erro={mapa.error} aoTentarDeNovo={() => void mapa.refetch()} />;
  const carregando = detalhe.isLoading || mapa.isLoading || ocupadosQ.isLoading;

  const selecionadas = [...selecao].map((l) => porId.get(l)).filter((p): p is Posicao => !!p);
  const comSaldo = selecionadas.filter((p) => p.saldo > 0).length;
  const saldo = selecionadas.reduce((s, p) => s + Math.max(0, p.saldo), 0);

  // Trocar de centro troca o mapa inteiro: a seleção do outro centro não vale aqui.
  const trocarCentro = (novo: number): void => {
    definirCentroEscolhido(novo);
    definirSelecao(new Set());
    definirRuaAberta(null);
    definirConfirmando(false);
  };

  const criarEscopo = (): void => {
    if (centro === null) return;
    const resumoRuas = resumoPorRua(selecionadas)
      .map((r) => `${r.nome}(${r.qtd})`)
      .join(' ');
    criar.mutate(
      { localizadores: [...selecao], descricao: resumoRuas.slice(0, 1900), centro },
      {
        onSuccess: (r) => navegar(`/inventario/${id}?novo=${r.escopo}`),
        onError: () => {
          definirConfirmando(false);
          void ocupadosQ.refetch();
        },
      },
    );
  };

  return (
    <div className="space-y-4 p-3 sm:p-5 sm:pl-8">
      <NavInventario inventario={id} nome={detalhe.data?.inventario.nome ?? 'Inventário'} podePlanejar />

      <CabecalhoPagina
        sobrelinha={`Novo escopo · centro ${centro ?? '…'}`}
        titulo="Montar escopo"
        descricao="Marque no mapa, filtre ou cole uma lista. Tudo vai para a mesma seleção, à direita; nada é gravado até você criar o escopo."
        icone={<MousePointerClick className="h-5 w-5" />}
        acoes={
          centrosDaEmpresa.length > 1 && centro !== null ? (
            <label className="flex items-center gap-2 text-[12.5px] text-textoFraco">
              Centro do escopo
              <select
                title="Centro de estoque do escopo"
                value={centro}
                onChange={(e) => trocarCentro(Number(e.target.value))}
                className="min-h-[34px] w-[230px] rounded-lg border border-borda bg-superficie px-2 text-[12.5px] font-semibold text-texto shadow-xs"
              >
                {centrosDaEmpresa.map((c) => (
                  <option key={c.centroEstoque} value={c.centroEstoque}>
                    {c.centroEstoque} · {c.nome}
                    {c.centroEstoque === detalhe.data?.inventario.centro ? ' (padrão)' : ''}
                  </option>
                ))}
              </select>
            </label>
          ) : undefined
        }
      />

      {carregando ? (
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_340px]">
          <Esqueleto className="h-[520px]" />
          <Esqueleto className="h-[520px]" />
        </div>
      ) : (
        <div className="grid items-start gap-3 lg:grid-cols-[minmax(0,1fr)_340px]">
          <Cartao className="overflow-hidden">
            <div className="flex gap-1 overflow-x-auto border-b border-borda bg-superficie2 p-1.5">
              <BotaoAba ativa={aba === 'mapa'} onClick={() => definirAba('mapa')} icone={<MapaIcone className="h-4 w-4" />}>
                Mapa
              </BotaoAba>
              <BotaoAba ativa={aba === 'filtros'} onClick={() => definirAba('filtros')} icone={<Filter className="h-4 w-4" />}>
                Filtros
              </BotaoAba>
              <BotaoAba ativa={aba === 'importar'} onClick={() => definirAba('importar')} icone={<ClipboardPaste className="h-4 w-4" />}>
                Importar lista
              </BotaoAba>
              <BotaoAba ativa={aba === 'produtos'} onClick={() => definirAba('produtos')} icone={<Package className="h-4 w-4" />}>
                Produtos
              </BotaoAba>
              <BotaoAba ativa={aba === 'escopo'} onClick={() => definirAba('escopo')} icone={<History className="h-4 w-4" />}>
                A partir de escopo
              </BotaoAba>
            </div>
            <div className="p-4">
              {aba === 'mapa' ? (
                <AbaMapa
                  centro={centro}
                  ruas={ruas}
                  selecao={selecao}
                  ocupados={ocupados}
                  aplicar={aplicar}
                  ruaAberta={ruaAberta}
                  definirRuaAberta={definirRuaAberta}
                />
              ) : aba === 'filtros' ? (
                <AbaFiltros ruas={ruas} posicoes={posicoes} selecao={selecao} ocupados={ocupados} aplicar={aplicar} />
              ) : aba === 'produtos' ? (
                <AbaProdutos inventario={id} centro={centro} selecao={selecao} ocupados={ocupados} aplicar={aplicar} />
              ) : aba === 'escopo' ? (
                <AbaDeEscopo
                  inventario={id}
                  escopos={(detalhe.data?.escopos ?? []).filter((e) => e.centro === centro)}
                  selecao={selecao}
                  ocupados={ocupados}
                  aplicar={aplicar}
                />
              ) : (
                <AbaImportar posicoes={posicoes} selecao={selecao} ocupados={ocupados} aplicar={aplicar} />
              )}
            </div>
          </Cartao>

          <Cartao className="lg:sticky lg:top-3">
            <div className="border-b border-borda px-4 py-3">
              <p className="rotulo">Neste escopo</p>
              <p className="tabular mt-1 text-[30px] font-bold leading-none tracking-[-0.03em]">
                {numero(selecao.size)}
                <span className="ml-1.5 text-sm font-medium text-textoFraco">
                  {plural(selecao.size, 'posição', 'posições')}
                </span>
              </p>
              <p className="mt-1.5 text-xs text-textoFraco">
                {numero(comSaldo)} com saldo · {numero(saldo)} un em estoque
              </p>
              {ritmo.data?.enderecosPorHora && selecao.size > 0 ? (
                <p
                  className="mt-1 flex items-center gap-1 text-xs text-textoFraco"
                  title={`Ritmo dos inventários do último ano: ${numero(ritmo.data.enderecosPorHora)} endereços por hora por contador (mediana)`}
                >
                  <Clock className="h-3.5 w-3.5" />
                  Contagem ≈ {formatarHoras(selecao.size / ritmo.data.enderecosPorHora)} com 1 pessoa ·{' '}
                  {formatarHoras(selecao.size / ritmo.data.enderecosPorHora / 3)} com 3
                </p>
              ) : null}
            </div>
            <div className="max-h-[46vh] overflow-y-auto px-2 py-2">
              {selecao.size === 0 ? (
                <p className="px-2 py-6 text-center text-sm text-textoSuave">
                  Nada marcado ainda. Comece clicando numa rua.
                </p>
              ) : (
                <ul className="space-y-1">
                  {resumoPorRua(selecionadas).map((r) => {
                    const total = ruas.find((x) => x.nome === r.nome)?.posicoes.length ?? r.qtd;
                    return (
                      <li
                        key={r.nome}
                        className="group flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-superficie2"
                      >
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">Rua {r.nome}</span>
                        <span className="tabular text-xs text-textoFraco">
                          {r.qtd === total ? 'inteira' : `${numero(r.qtd)} de ${numero(total)}`}
                        </span>
                        <button
                          type="button"
                          onClick={() => aplicar(r.ids, 'remove')}
                          className="rounded p-1 text-textoSuave opacity-60 hover:bg-superficie3 hover:text-status-divergencia group-hover:opacity-100"
                          aria-label={`Tirar a rua ${r.nome}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="space-y-2 border-t border-borda p-3">
              {selecionadosEmOutro > 0 ? (
                <p className="flex items-start gap-1.5 text-xs text-status-atencao">
                  <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {numero(selecionadosEmOutro)}{' '}
                  {selecionadosEmOutro === 1 ? 'endereço marcado já está' : 'endereços marcados já estão'} em outro
                  escopo aberto. Entram mesmo assim; cuidado para não integrar o mesmo endereço duas vezes.
                </p>
              ) : null}
              <Executando ativo={criar.isPending} oQue="Criando o escopo" />
              {criar.isError ? (
                <p className="rounded-lg bg-status-divergenciaFundo px-3 py-2 text-xs text-status-divergencia">
                  {criar.error instanceof ErroHttp ? criar.error.message : 'Não foi possível criar o escopo.'}
                </p>
              ) : null}
              {confirmando ? (
                <div className="space-y-2 rounded-lg border border-primaria/30 bg-primaria-suave p-3">
                  <p className="text-sm">
                    Grava no ERP um escopo novo no centro {centro} com{' '}
                    <b>{numero(selecao.size)} endereços</b>. Os produtos desses endereços entram
                    automaticamente, como no botão "Insere Produtos Localizador" do PBS.
                  </p>
                  <div className="flex gap-2">
                    <Botao variante="primaria" tamanho="pequeno" carregando={criar.isPending} onClick={criarEscopo}>
                      <Check className="h-4 w-4" />
                      Confirmar
                    </Botao>
                    <Botao variante="fantasma" tamanho="pequeno" onClick={() => definirConfirmando(false)}>
                      Voltar
                    </Botao>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Botao
                    variante="primaria"
                    className="flex-1"
                    disabled={selecao.size === 0}
                    onClick={() => definirConfirmando(true)}
                  >
                    Criar escopo
                  </Botao>
                  <Botao
                    variante="fantasma"
                    disabled={selecao.size === 0}
                    onClick={() => definirSelecao(new Set())}
                    aria-label="Limpar seleção"
                    title="Limpar seleção"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Botao>
                </div>
              )}
            </div>
          </Cartao>
        </div>
      )}
    </div>
  );
}

function formatarHoras(h: number): string {
  if (h < 1) return `${Math.max(5, Math.round((h * 60) / 5) * 5)} min`;
  return `${h < 10 ? h.toFixed(1).replace('.', ',') : Math.round(h)} h`;
}

function resumoPorRua(ps: Posicao[]): { nome: string; qtd: number; ids: number[] }[] {
  const g = new Map<string, number[]>();
  for (const p of ps) {
    const k = chaveRua(p);
    const l = g.get(k) ?? [];
    l.push(p.localizador);
    g.set(k, l);
  }
  return [...g.entries()]
    .sort(([a], [b]) => compararNatural(a, b))
    .map(([nome, ids]) => ({ nome, qtd: ids.length, ids }));
}

function BotaoAba({
  ativa,
  icone,
  children,
  onClick,
}: {
  ativa: boolean;
  icone: React.ReactNode;
  children: React.ReactNode;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex min-h-[36px] shrink-0 items-center whitespace-nowrap gap-1.5 rounded-lg px-3 text-[13px] font-medium transition-colors',
        ativa ? 'bg-superficie text-texto shadow-xs ring-1 ring-borda' : 'text-textoFraco hover:text-texto',
      )}
    >
      {icone}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Mapa: ruas clicáveis e arrastáveis; a rua aberta vira o rack
// ---------------------------------------------------------------------------

interface PropsSelecao {
  selecao: Set<number>;
  ocupados: Map<number, EnderecoOcupado>;
  aplicar: (ids: Iterable<number>, modo: 'add' | 'remove') => void;
}

function ids(ps: Posicao[]): number[] {
  return ps.map((p) => p.localizador);
}

function AbaMapa({
  centro,
  ruas,
  selecao,
  ocupados,
  aplicar,
  ruaAberta,
  definirRuaAberta,
}: PropsSelecao & {
  centro: number | null;
  ruas: Rua[];
  ruaAberta: string | null;
  definirRuaAberta: (r: string | null) => void;
}): JSX.Element {
  // Arrastar pinta: o modo (marcar ou desmarcar) é decidido pela primeira rua.
  const pintando = React.useRef<'add' | 'remove' | null>(null);
  React.useEffect(() => {
    const soltar = (): void => {
      pintando.current = null;
    };
    window.addEventListener('pointerup', soltar);
    return () => window.removeEventListener('pointerup', soltar);
  }, []);

  const rua = ruas.find((r) => r.nome === ruaAberta) ?? null;

  return (
    <div className="space-y-4">
      <p className="text-xs text-textoFraco">
        Clique numa rua para marcar inteira; segure e arraste para marcar várias. Use{' '}
        <b>abrir</b> para escolher módulo, nível ou vaga.
      </p>
      <div className="grid select-none grid-cols-[repeat(auto-fill,minmax(112px,1fr))] gap-2">
        {ruas.map((r) => {
          const disponiveis = ids(r.posicoes);
          const marcadas = r.posicoes.filter((p) => selecao.has(p.localizador)).length;
          const emOutro = r.posicoes.filter((p) => ocupados.has(p.localizador)).length;
          const cheia = disponiveis.length > 0 && marcadas === disponiveis.length;
          const parcial = marcadas > 0 && !cheia;
          const toda = disponiveis.length === 0;
          return (
            <div
              key={r.nome}
              role="button"
              tabIndex={0}
              aria-pressed={cheia}
              onPointerDown={(e) => {
                if ((e.target as HTMLElement).closest('[data-abrir]')) return;
                if (toda) return;
                const modo = cheia ? 'remove' : 'add';
                pintando.current = modo;
                aplicar(disponiveis, modo);
              }}
              onPointerEnter={() => {
                if (pintando.current && !toda) aplicar(disponiveis, pintando.current);
              }}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !toda) {
                  e.preventDefault();
                  aplicar(disponiveis, cheia ? 'remove' : 'add');
                }
              }}
              className={cn(
                'relative cursor-pointer rounded-xl border p-2.5 transition-all',
                cheia
                  ? 'border-primaria bg-primaria text-white shadow-sm'
                  : parcial
                    ? 'border-primaria bg-primaria-suave'
                    : toda
                      ? 'cursor-not-allowed border-dashed border-borda bg-superficie2 opacity-60'
                      : 'border-borda bg-superficie hover:border-bordaForte hover:shadow-xs',
                ruaAberta === r.nome && 'ring-2 ring-primaria ring-offset-2 ring-offset-superficie',
              )}
              title={emOutro > 0 ? `${emOutro} posições também estão em outro escopo aberto` : undefined}
            >
              <div className="flex items-center justify-between gap-1">
                <span className="truncate text-[13px] font-bold">{r.nome}</span>
                {cheia ? <Check className="h-3.5 w-3.5 shrink-0" /> : null}
              </div>
              <p className={cn('tabular mt-0.5 text-2xs', cheia ? 'text-white/85' : 'text-textoFraco')}>
                {marcadas > 0 && !cheia ? `${numero(marcadas)} / ` : ''}
                {numero(r.posicoes.length)} pos.
                {emOutro > 0 ? ` · ${numero(emOutro)} em outro` : ''}
              </p>
              <button
                type="button"
                data-abrir
                onClick={() => definirRuaAberta(ruaAberta === r.nome ? null : r.nome)}
                className={cn(
                  'mt-1.5 w-full rounded-md py-0.5 text-2xs font-semibold',
                  cheia ? 'bg-white/15 hover:bg-white/25' : 'bg-superficie3/70 text-textoFraco hover:bg-superficie3 hover:text-texto',
                )}
              >
                {ruaAberta === r.nome ? 'fechar' : 'abrir'}
              </button>
            </div>
          );
        })}
      </div>

      {rua ? (
        <RackDaRua centro={centro} rua={rua} selecao={selecao} ocupados={ocupados} aplicar={aplicar} aoFechar={() => definirRuaAberta(null)} />
      ) : null}
    </div>
  );
}

/**
 * A rua como o operador vê de frente: módulos da esquerda para a direita,
 * níveis de baixo para cima. Clicar no cabeçalho do módulo marca a coluna;
 * no rótulo do nível, a linha; na célula, as vagas dela.
 */
function RackDaRua({
  centro,
  rua,
  selecao,
  ocupados,
  aplicar,
  aoFechar,
}: PropsSelecao & { centro: number | null; rua: Rua; aoFechar: () => void }): JSX.Element {
  const { hover, props: hoverDe } = useHoverCartao<Posicao[]>();
  const comGeometria = rua.posicoes.filter((p) => p.geometriaConfiavel);
  const semGeometria = rua.posicoes.filter((p) => !p.geometriaConfiavel);
  const modulos = [...new Set(comGeometria.map((p) => p.moduloOrdem))].sort((a, b) => a - b);
  const niveis = [...new Set(comGeometria.map((p) => p.nivelRack))].sort((a, b) => b - a);
  const celula = new Map<string, Posicao[]>();
  for (const p of comGeometria) {
    const k = `${p.moduloOrdem}|${p.nivelRack}`;
    const l = celula.get(k) ?? [];
    l.push(p);
    celula.set(k, l);
  }
  const alternar = (ps: Posicao[]): void => {
    const disp = ids(ps);
    if (disp.length === 0) return;
    const todas = disp.every((l) => selecao.has(l));
    aplicar(disp, todas ? 'remove' : 'add');
  };

  return (
    <div className="rounded-xl border border-borda bg-superficie2/60 p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-semibold">
          Rua {rua.nome}{' '}
          <span className="font-normal text-textoFraco">
            · {numero(modulos.length)} módulos × {numero(niveis.length)} níveis
          </span>
        </p>
        <Botao variante="fantasma" tamanho="micro" onClick={aoFechar}>
          <X className="h-3.5 w-3.5" />
        </Botao>
      </div>
      {comGeometria.length > 0 ? (
        <div className="overflow-x-auto pb-1">
          <table className="border-separate border-spacing-1">
            <thead>
              <tr>
                <th />
                {modulos.map((m) => (
                  <th key={m}>
                    <button
                      type="button"
                      onClick={() => alternar(comGeometria.filter((p) => p.moduloOrdem === m))}
                      className="tabular w-8 rounded text-2xs font-semibold text-textoFraco hover:bg-superficie3 hover:text-texto"
                      title={`Módulo ${m} inteiro`}
                    >
                      {m}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {niveis.map((n) => (
                <tr key={n}>
                  <th>
                    <button
                      type="button"
                      onClick={() => alternar(comGeometria.filter((p) => p.nivelRack === n))}
                      className="tabular w-9 rounded px-1 text-right text-2xs font-semibold text-textoFraco hover:bg-superficie3 hover:text-texto"
                      title={`Nível ${n} na rua inteira`}
                    >
                      {n === 0 ? 'piso' : `N${n}`}
                    </button>
                  </th>
                  {modulos.map((m) => {
                    const ps = celula.get(`${m}|${n}`) ?? [];
                    if (ps.length === 0) return <td key={m} className="h-7 w-8" />;
                    const bloq = ps.filter((p) => ocupados.has(p.localizador));
                    const marc = ps.filter((p) => selecao.has(p.localizador)).length;
                    const disp = ps.length;
                    const cheia = disp > 0 && marc === disp;
                    const temSaldo = ps.some((p) => p.saldo > 0);
                    return (
                      <td key={m} {...hoverDe(ps)}>
                        <button
                          type="button"
                          onClick={() => alternar(ps)}
                          aria-label={
                            ps.map((p) => p.descricao).join(', ') +
                            (bloq.length > 0
                              ? ` — também no escopo ${ocupados.get(bloq[0]!.localizador)?.escopo}`
                              : temSaldo
                                ? ' — com saldo'
                                : ' — vazia')
                          }
                          className={cn(
                            'relative h-7 w-8 rounded-md border transition-colors',
                            cheia
                              ? 'border-primaria bg-primaria'
                              : marc > 0
                                ? 'border-primaria bg-primaria/40'
                                : 'border-borda bg-superficie hover:border-primaria',
                            bloq.length > 0 && 'border-dashed !border-status-atencao',
                          )}
                        >
                          {temSaldo && disp > 0 ? (
                            <span
                              className={cn(
                                'absolute right-1 top-1 h-1.5 w-1.5 rounded-full',
                                cheia ? 'bg-white' : 'bg-status-livre',
                              )}
                            />
                          ) : null}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-2xs text-textoFraco">
            <span className="inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-status-livre" /> com saldo
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-2.5 w-3 rounded-sm border border-dashed border-status-atencao" /> também em outro
              escopo
            </span>
            <span>Clique no número do módulo ou no nível para marcar a coluna ou a linha.</span>
          </p>
        </div>
      ) : null}
      {semGeometria.length > 0 ? (
        <div className="mt-3">
          <p className="rotulo mb-1.5">Posições fora do padrão de rack</p>
          <div className="flex flex-wrap gap-1">
            {semGeometria.map((p) => {
              const bloq = ocupados.has(p.localizador);
              const marc = selecao.has(p.localizador);
              return (
                <button
                  key={p.localizador}
                  {...hoverDe([p])}
                  type="button"
                  onClick={() => aplicar([p.localizador], marc ? 'remove' : 'add')}
                  title={bloq ? `Também no escopo ${ocupados.get(p.localizador)?.escopo}` : undefined}
                  className={cn(
                    'rounded-md border px-2 py-0.5 font-mono text-2xs',
                    marc ? 'border-primaria bg-primaria text-white' : 'border-borda bg-superficie hover:border-primaria',
                    bloq && 'border-dashed !border-status-atencao',
                  )}
                >
                  {p.descricao}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      {hover && centro !== null ? (
        <DicaDaPosicao centro={centro} posicoes={hover.alvo} ponto={hover} ocupados={ocupados} />
      ) : null}
    </div>
  );
}

/**
 * Hover da vaga no montador: o saldo de agora, por SKU — é o que vai ser
 * contado se o endereço entrar no escopo. Mesma consulta da gaveta de posição
 * (`/api/posicao`), com a mesma chave de cache.
 */
function DicaDaPosicao({
  centro,
  posicoes,
  ponto,
  ocupados,
}: {
  centro: number;
  posicoes: Posicao[];
  ponto: PontoCartao;
  ocupados: Map<number, EnderecoOcupado>;
}): JSX.Element {
  const visiveis = posicoes.slice(0, 4);
  const consultas = useQueries({
    queries: visiveis.map((p) => ({
      queryKey: ['posicao', centro, p.localizador],
      queryFn: () => api.get<PosicaoResposta>(`/api/posicao/${p.localizador}`, { centro }),
      staleTime: 60_000,
    })),
  });
  const bloq = posicoes.find((p) => ocupados.has(p.localizador));
  return (
    <CartaoEndereco
      ponto={ponto}
      titulo="Vai ser contado"
      etiqueta={
        bloq ? (
          <span className="rounded-md bg-white/10 px-1.5 py-0.5 text-[10.5px] font-semibold text-amber-200">
            também no escopo {ocupados.get(bloq.localizador)?.escopo}
          </span>
        ) : undefined
      }
      rodape={posicoes.length > visiveis.length ? `+ ${posicoes.length - visiveis.length} vagas nesta célula` : undefined}
      enderecos={visiveis.map((p, i) => {
        const d = consultas[i]?.data;
        return {
          localizador: p.localizador,
          nome: p.descricao,
          linhas: d
            ? d.itens
                .filter((x) => x.saldo !== 0)
                .map((x) => ({ produto: x.produto, descricao: x.descricao, quantidade: x.saldo }))
            : consultas[i]?.isError
              ? []
              : null,
        };
      })}
    />
  );
}

// ---------------------------------------------------------------------------
// Filtros
// ---------------------------------------------------------------------------

function AbaFiltros({
  ruas,
  posicoes,
  selecao,
  ocupados,
  aplicar,
}: PropsSelecao & { ruas: Rua[]; posicoes: Posicao[] }): JSX.Element {
  const nomes = ruas.map((r) => r.nome);
  const [de, definirDe] = React.useState(nomes[0] ?? '');
  const [ate, definirAte] = React.useState(nomes[nomes.length - 1] ?? '');
  const zonas = React.useMemo(() => [...new Set(posicoes.map((p) => p.zona))].sort(compararNatural), [posicoes]);
  const niveis = React.useMemo(
    () => [...new Set(posicoes.filter((p) => p.geometriaConfiavel).map((p) => p.nivelRack))].sort((a, b) => a - b),
    [posicoes],
  );
  const [zonasSel, definirZonasSel] = React.useState<Set<string>>(new Set());
  const [niveisSel, definirNiveisSel] = React.useState<Set<number>>(new Set());
  const [soSaldo, definirSoSaldo] = React.useState(false);

  const iDe = nomes.indexOf(de);
  const iAte = nomes.indexOf(ate);
  const [ini, fim] = iDe <= iAte ? [iDe, iAte] : [iAte, iDe];
  const ruasNaFaixa = new Set(nomes.slice(ini, fim + 1));

  const resultado = posicoes.filter(
    (p) =>
      ruasNaFaixa.has(chaveRua(p)) &&
      (zonasSel.size === 0 || zonasSel.has(p.zona)) &&
      (niveisSel.size === 0 || (p.geometriaConfiavel && niveisSel.has(p.nivelRack))) &&
      (!soSaldo || p.saldo > 0),
  );
  const disponiveis = ids(resultado);
  const emOutro = resultado.filter((p) => ocupados.has(p.localizador)).length;
  const novas = disponiveis.filter((l) => !selecao.has(l)).length;
  const jaMarcadas = disponiveis.length - novas;

  const alternarEm = <T,>(conj: Set<T>, v: T, definir: (s: Set<T>) => void): void => {
    const n = new Set(conj);
    if (n.has(v)) n.delete(v);
    else n.add(v);
    definir(n);
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <label>
          <span className="rotulo mb-1 block">Da rua</span>
          <select
            value={de}
            onChange={(e) => definirDe(e.target.value)}
            className="min-h-toque w-full rounded-lg border border-borda bg-superficie px-3 text-sm"
          >
            {nomes.map((n) => (
              <option key={n}>{n}</option>
            ))}
          </select>
        </label>
        <label>
          <span className="rotulo mb-1 block">Até a rua</span>
          <select
            value={ate}
            onChange={(e) => definirAte(e.target.value)}
            className="min-h-toque w-full rounded-lg border border-borda bg-superficie px-3 text-sm"
          >
            {nomes.map((n) => (
              <option key={n}>{n}</option>
            ))}
          </select>
        </label>
      </div>

      <div>
        <p className="rotulo mb-1.5">Zona {zonasSel.size === 0 ? '(todas)' : ''}</p>
        <div className="flex flex-wrap gap-1.5">
          {zonas.map((z) => (
            <Chip key={z} ativo={zonasSel.has(z)} onClick={() => alternarEm(zonasSel, z, definirZonasSel)}>
              {z}
            </Chip>
          ))}
        </div>
      </div>

      <div>
        <p className="rotulo mb-1.5">Nível {niveisSel.size === 0 ? '(todos)' : ''}</p>
        <div className="flex flex-wrap gap-1.5">
          {niveis.map((n) => (
            <Chip key={n} ativo={niveisSel.has(n)} onClick={() => alternarEm(niveisSel, n, definirNiveisSel)}>
              {n === 0 ? 'Piso' : `N${n}`}
            </Chip>
          ))}
        </div>
      </div>

      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <input type="checkbox" checked={soSaldo} onChange={(e) => definirSoSaldo(e.target.checked)} className="h-4 w-4" />
        Só posições com saldo
      </label>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-borda bg-superficie2 p-3">
        <p className="text-sm">
          <b className="tabular">{numero(resultado.length)}</b> posições no filtro
          {emOutro > 0 ? (
            <span className="text-status-atencao"> · {numero(emOutro)} também em outro escopo</span>
          ) : null}
          {jaMarcadas > 0 ? <span className="text-textoFraco"> · {numero(jaMarcadas)} já marcadas</span> : null}
        </p>
        <div className="flex gap-2">
          <Botao variante="secundaria" tamanho="pequeno" disabled={jaMarcadas === 0} onClick={() => aplicar(disponiveis, 'remove')}>
            Tirar
          </Botao>
          <Botao variante="primaria" tamanho="pequeno" disabled={novas === 0} onClick={() => aplicar(disponiveis, 'add')}>
            Adicionar {numero(novas)}
          </Botao>
        </div>
      </div>
    </div>
  );
}

function Chip({
  ativo,
  onClick,
  children,
}: {
  ativo: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={ativo}
      className={cn(
        'min-h-[32px] rounded-full border px-3 text-xs font-medium transition-colors',
        ativo ? 'border-primaria bg-primaria text-white' : 'border-borda bg-superficie hover:border-bordaForte',
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Importar lista
// ---------------------------------------------------------------------------

/**
 * Aceita o que a operação tem na mão: coluna copiada do Excel, mensagem do
 * WhatsApp, CSV. Cada pedaço é procurado pelo número do localizador e pelo
 * endereço escrito (`MC003-01-02`), sem diferenciar maiúscula.
 */
function AbaImportar({
  posicoes,
  selecao,
  ocupados,
  aplicar,
}: PropsSelecao & { posicoes: Posicao[] }): JSX.Element {
  const [texto, definirTexto] = React.useState('');
  const arquivo = React.useRef<HTMLInputElement>(null);

  const indice = React.useMemo(() => {
    const m = new Map<string, Posicao>();
    for (const p of posicoes) {
      m.set(String(p.localizador), p);
      m.set(p.descricao.trim().toUpperCase(), p);
    }
    return m;
  }, [posicoes]);

  const analise = React.useMemo(() => {
    const pedacos = texto
      .split(/[\n\r,;\t]+/)
      .map((t) => t.trim().replace(/^"|"$/g, ''))
      .filter(Boolean);
    const achados = new Map<number, Posicao>();
    const naoAchados: string[] = [];
    let repetidos = 0;
    for (const t of pedacos) {
      const p = indice.get(t.toUpperCase()) ?? indice.get(t.replace(/\D/g, '') || '¤');
      if (!p) {
        // Linha de CSV com várias colunas: tenta cada palavra antes de desistir.
        const palavra = t
          .split(/\s+/)
          .map((w) => indice.get(w.toUpperCase()))
          .find(Boolean);
        if (palavra) {
          if (achados.has(palavra.localizador)) repetidos++;
          achados.set(palavra.localizador, palavra);
        } else naoAchados.push(t);
        continue;
      }
      if (achados.has(p.localizador)) repetidos++;
      achados.set(p.localizador, p);
    }
    const lista = [...achados.values()];
    const bloqueados = lista.filter((p) => ocupados.has(p.localizador));
    const jaMarcados = lista.filter((p) => selecao.has(p.localizador));
    const novos = lista.filter((p) => !selecao.has(p.localizador));
    return { total: pedacos.length, novos, jaMarcados, bloqueados, naoAchados, repetidos };
  }, [texto, indice, ocupados, selecao]);

  const lerArquivo = async (f: File): Promise<void> => {
    definirTexto(await f.text());
  };

  return (
    <div className="space-y-4">
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files[0];
          if (f) void lerArquivo(f);
        }}
      >
        <textarea
          value={texto}
          onChange={(e) => definirTexto(e.target.value)}
          rows={8}
          placeholder={'Cole aqui os endereços ou números de localizador — um por linha, ou separados por vírgula.\nEx.:\nMC003-01-02\nMC003-01-03\n55943'}
          className="w-full rounded-lg border border-borda bg-superficie p-3 font-mono text-sm focus:border-primaria focus:outline-none focus:ring-4 focus:ring-primaria/12"
        />
        <div className="mt-2 flex items-center gap-2">
          <input
            ref={arquivo}
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void lerArquivo(f);
              e.target.value = '';
            }}
          />
          <Botao variante="secundaria" tamanho="pequeno" onClick={() => arquivo.current?.click()}>
            <Upload className="h-4 w-4" />
            Abrir CSV ou TXT
          </Botao>
          <span className="text-xs text-textoSuave">ou arraste o arquivo para a caixa</span>
          {texto ? (
            <Botao variante="fantasma" tamanho="pequeno" className="ml-auto" onClick={() => definirTexto('')}>
              Limpar
            </Botao>
          ) : null}
        </div>
      </div>

      {analise.total > 0 ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Contagem rotulo="Novos" valor={analise.novos.length} tom="ok" />
            <Contagem rotulo="Já marcados" valor={analise.jaMarcados.length} />
            <Contagem rotulo="Em outro escopo" valor={analise.bloqueados.length} tom={analise.bloqueados.length ? 'alerta' : undefined} />
            <Contagem rotulo="Não encontrados" valor={analise.naoAchados.length} tom={analise.naoAchados.length ? 'erro' : undefined} />
          </div>
          {analise.naoAchados.length > 0 ? (
            <details className="text-xs">
              <summary className="cursor-pointer text-textoFraco">
                Ver os {numero(analise.naoAchados.length)} que não existem neste centro
              </summary>
              <p className="mt-1 break-words font-mono text-textoFraco">{analise.naoAchados.slice(0, 200).join(' · ')}</p>
            </details>
          ) : null}
          {analise.bloqueados.length > 0 ? (
            <p className="text-xs text-textoFraco">
              Também em outro escopo aberto (entram mesmo assim):{' '}
              <span className="font-mono">
                {analise.bloqueados
                  .slice(0, 30)
                  .map((p) => `${p.descricao} (escopo ${ocupados.get(p.localizador)?.escopo})`)
                  .join(' · ')}
              </span>
            </p>
          ) : null}
          <Botao
            variante="primaria"
            disabled={analise.novos.length === 0}
            onClick={() => {
              aplicar(
                analise.novos.map((p) => p.localizador),
                'add',
              );
              definirTexto('');
            }}
          >
            Adicionar {numero(analise.novos.length)} {plural(analise.novos.length, 'endereço', 'endereços')}
          </Botao>
        </div>
      ) : null}
    </div>
  );
}

function Contagem({
  rotulo,
  valor,
  tom,
}: {
  rotulo: string;
  valor: number;
  tom?: 'ok' | 'alerta' | 'erro' | undefined;
}): JSX.Element {
  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2',
        tom === 'ok' && valor > 0 && 'border-status-livre/30 bg-status-livreFundo',
        tom === 'alerta' && 'border-status-atencao/30 bg-status-atencaoFundo',
        tom === 'erro' && 'border-status-divergencia/30 bg-status-divergenciaFundo',
        !tom && 'border-borda',
      )}
    >
      <p className="text-2xs text-textoFraco">{rotulo}</p>
      <p className="tabular text-lg font-bold">{numero(valor)}</p>
    </div>
  );
}
